import React, { useCallback, useEffect, useState } from "react";

/**
 * AdminJerichoConsole — live operators list + recent audit timeline.
 *
 * Mounted under /admin/jericho. Uses the admin operator token (the
 * existing break-glass channel the rest of /admin already uses).
 *
 * Requirements:
 *   - Polls /api/admin/jericho/operators every 5s.
 *   - Shows callsign / role / approximate IP location / device /
 *     entered / active duration / current status.
 *   - Owner-only emergency override panel emits POST
 *     /api/admin/jericho/override and surfaces the audit row.
 */
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_MS = 5000;

function _humanDuration(ms) {
  if (ms == null) return "—";
  const s = Math.max(0, Math.floor(ms / 1000));
  const mm = Math.floor(s / 60);
  const ss = s % 60;
  return `${mm}m ${String(ss).padStart(2, "0")}s`;
}

export const AdminJerichoConsole = ({ opToken, userToken }) => {
  const [operators, setOperators] = useState([]);
  const [audit, setAudit] = useState([]);
  const [generatedAt, setGeneratedAt] = useState(null);
  const [overrideEmail, setOverrideEmail] = useState("");
  const [overrideReason, setOverrideReason] = useState("");
  const [overrideResult, setOverrideResult] = useState(null);
  const [overrideBusy, setOverrideBusy] = useState(false);

  const _headers = useCallback(() => {
    const h = {};
    if (opToken) h["x-admin-token"] = opToken;
    if (userToken) h["Authorization"] = `Bearer ${userToken}`;
    return h;
  }, [opToken, userToken]);

  const refresh = useCallback(async () => {
    try {
      const [ops, aud] = await Promise.all([
        fetch(`${API}/admin/jericho/operators`, { headers: _headers() }),
        fetch(`${API}/admin/jericho/audit?limit=50`, { headers: _headers() }),
      ]);
      if (ops.ok) {
        const data = await ops.json();
        setOperators(data.operators || []);
        setGeneratedAt(data.generated_at);
      }
      if (aud.ok) {
        const data = await aud.json();
        setAudit(data.audit || []);
      }
    } catch (_) {
      /* poll silently fails */
    }
  }, [_headers]);

  useEffect(() => {
    refresh();
    const id = window.setInterval(refresh, POLL_MS);
    return () => window.clearInterval(id);
  }, [refresh]);

  const submitOverride = async (e) => {
    e?.preventDefault?.();
    if (overrideBusy) return;
    setOverrideBusy(true);
    setOverrideResult(null);
    try {
      const r = await fetch(`${API}/admin/jericho/override`, {
        method: "POST",
        headers: { ..._headers(), "Content-Type": "application/json" },
        body: JSON.stringify({
          user_email: overrideEmail.trim(),
          reason: overrideReason.trim(),
        }),
      });
      const data = await r.json().catch(() => ({}));
      setOverrideResult({ ok: r.ok, ...data });
      if (r.ok) {
        setOverrideEmail("");
        setOverrideReason("");
        refresh();
      }
    } catch (_) {
      setOverrideResult({ ok: false, detail: "Request failed" });
    } finally {
      setOverrideBusy(false);
    }
  };

  return (
    <div data-testid="admin-jericho-console" className="px-4 sm:px-8 py-6">
      <div className="flex items-baseline justify-between mb-6">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.35em] text-[#D92D20]">
            // RESTRICTED ACCESS NODE
          </div>
          <h1 className="font-display text-3xl sm:text-4xl text-[#E4DFD3] mt-1">
            NODE JERICHO · Active Operators
          </h1>
        </div>
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#7a8294]">
          last sync: {generatedAt ? new Date(generatedAt).toLocaleTimeString() : "—"}
        </div>
      </div>

      <div className="border border-[#2D2A26] bg-[#0F0F0F]">
        <div className="grid grid-cols-12 px-4 py-2 font-mono text-[9px] uppercase tracking-[0.3em] text-[#7a8294] border-b border-[#2D2A26]">
          <div className="col-span-2">Callsign</div>
          <div className="col-span-2">Role</div>
          <div className="col-span-2">Location</div>
          <div className="col-span-2">Device</div>
          <div className="col-span-2">Entered</div>
          <div className="col-span-1">Active</div>
          <div className="col-span-1 text-right">Status</div>
        </div>
        {operators.length === 0 && (
          <div className="px-4 py-6 font-mono text-[11px] text-[#7a8294]" data-testid="jericho-empty">
            // no active operators
          </div>
        )}
        {operators.map((op) => {
          const loc = op.ip_location || {};
          const locLabel = [loc.city, loc.region, loc.country].filter(Boolean).join(" · ") || "—";
          const entered = op.issued_at ? new Date(op.issued_at).toLocaleTimeString() : "—";
          return (
            <div
              key={op.session_id}
              data-testid={`jericho-op-${op.session_id}`}
              className="grid grid-cols-12 px-4 py-3 border-b border-[#2D2A26] text-sm text-[#E4DFD3]"
            >
              <div className="col-span-2 font-mono text-[#E0A526]">{op.callsign || "—"}</div>
              <div className="col-span-2 font-mono text-[11px] uppercase tracking-widest">{op.role}</div>
              <div className="col-span-2 font-mono text-[11px] text-[#A39E93]">{locLabel}</div>
              <div className="col-span-2 font-mono text-[11px] text-[#A39E93]">
                {op.device}/{op.os}/{op.browser}
              </div>
              <div className="col-span-2 font-mono text-[11px] text-[#A39E93]">{entered}</div>
              <div className="col-span-1 font-mono text-[11px] text-[#A39E93]">
                {_humanDuration(op.duration_ms)}
              </div>
              <div className="col-span-1 text-right font-mono text-[10px] uppercase">
                <span
                  className={
                    op.status === "active"
                      ? "text-[#5DD4B3]"
                      : "text-[#7a8294]"
                  }
                >
                  ● {op.status}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Owner override — guarded server-side; UI surfaces it unconditionally */}
      <details className="mt-8 border border-[#D92D20]/40 bg-[#1a0a08]">
        <summary className="px-4 py-3 cursor-pointer font-mono text-[10px] uppercase tracking-[0.35em] text-[#D92D20]">
          // OWNER OVERRIDE · emergency unlock
        </summary>
        <form onSubmit={submitOverride} className="p-4 grid sm:grid-cols-2 gap-3">
          <input
            type="email"
            value={overrideEmail}
            onChange={(e) => setOverrideEmail(e.target.value)}
            placeholder="operator email"
            data-testid="jericho-override-email"
            className="bg-black border border-[#454038] focus:border-[#D92D20] outline-none px-3 py-2 font-mono text-[12px] text-[#E4DFD3]"
          />
          <input
            type="text"
            value={overrideReason}
            onChange={(e) => setOverrideReason(e.target.value)}
            placeholder="reason (audited) — min 8 chars"
            minLength={8}
            data-testid="jericho-override-reason"
            className="bg-black border border-[#454038] focus:border-[#D92D20] outline-none px-3 py-2 font-mono text-[12px] text-[#E4DFD3]"
          />
          <button
            type="submit"
            disabled={overrideBusy || !overrideEmail || overrideReason.trim().length < 8}
            data-testid="jericho-override-submit"
            className="sm:col-span-2 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.35em] bg-[#D92D20] hover:bg-[#B92318] text-white disabled:opacity-50"
          >
            ISSUE OVERRIDE SESSION
          </button>
          {overrideResult && (
            <div
              data-testid="jericho-override-result"
              className={`sm:col-span-2 font-mono text-[10px] uppercase tracking-[0.3em] ${
                overrideResult.ok ? "text-[#5DD4B3]" : "text-[#D92D20]"
              }`}
            >
              {overrideResult.ok
                ? `// override issued — session ${overrideResult.session_id}`
                : `// ${overrideResult.detail || "denied"}`}
            </div>
          )}
        </form>
      </details>

      <div className="mt-10">
        <div className="font-mono text-[10px] uppercase tracking-[0.35em] text-[#D92D20] mb-3">
          // recent audit trail
        </div>
        <div className="border border-[#2D2A26] bg-[#0F0F0F] divide-y divide-[#2D2A26]">
          {audit.length === 0 && (
            <div className="px-4 py-4 font-mono text-[11px] text-[#7a8294]">// empty</div>
          )}
          {audit.map((row, i) => (
            <div
              key={i}
              data-testid={`jericho-audit-${i}`}
              className="px-4 py-2 grid grid-cols-12 gap-2 text-[11px] font-mono text-[#A39E93]"
            >
              <div className="col-span-2 text-[#7a8294]">
                {row.ts ? new Date(row.ts).toLocaleTimeString() : "—"}
              </div>
              <div className="col-span-2 text-[#E4DFD3]">{row.event}</div>
              <div className="col-span-2">{row.callsign || row.email || row.user_id || "—"}</div>
              <div className="col-span-1">{row.role || "—"}</div>
              <div className="col-span-2">
                {[row.city, row.country].filter(Boolean).join(" · ") || "—"}
              </div>
              <div className="col-span-1">{row.device || "—"}</div>
              <div className="col-span-1">{row.unlock_method || "—"}</div>
              <div className="col-span-1 text-right">
                <span className={row.success ? "text-[#5DD4B3]" : "text-[#D92D20]"}>
                  {row.success ? "OK" : (row.reason || "FAIL")}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AdminJerichoConsole;
